import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { CustomTextField } from '../../../../../components';
import { ValidationErrorNotice } from '../../../../../components/Notice';
import { AddListItemButton } from '../../../../../components/SelectableTile';
import Subheader from '../../../../../components/Subheader';
import DebouncedInput from '../../../../../edit/DebouncedInput';
import { CustomQuestionTypeOptions, CustomQuestionTypes } from '../../../constants';
import { multipleSelectionQuestionRequirements, serializeErrors } from '../../../helpers';
import { MultipleSelectionCustomChoice } from '../shared/CustomChoice';
import ChoiceRequirementsSelect from './ChoiceRequirementsSelect';

function MultipleSelectionConfiguration({
  wevo,
  customQuestion,
  showHeading = true,
  onCustomQuestionChanged,
  errors,
}) {
  const [deleteCounter, setDeleteCounter] = useState(0);

  const type = customQuestion?.question?.type;
  const labelsType = customQuestion?.question?.labelsType;

  const questionTypeLimits = CustomQuestionTypeOptions.find((option) => option.value === type)?.choiceLimits;

  const defaultChoices = questionTypeLimits?.default || 0;
  const minChoices = questionTypeLimits?.min || 0;
  const maxChoices = questionTypeLimits?.max || 0;

  const labels =
    customQuestion?.question?.labels || new Array(defaultChoices).fill(defaultChoices).map((_) => '');

  const canAddOptions = labels.length < maxChoices;
  const isDeleteable = [CustomQuestionTypes.MultiSelect].includes(type);
  const canDeleteChoices = minChoices < maxChoices && labels.length > minChoices;

  const handleChoiceAdded = () => {
    const newLabels = labels.concat(['']);

    const newChoiceRequirements = multipleSelectionQuestionRequirements({
      rangeType: customQuestion?.question?.rangeType,
      number: customQuestion?.question?.number,
      choices: newLabels,
    });

    onCustomQuestionChanged({
      customQuestion,
      updateFields: { labels: newLabels, labelsType, ...newChoiceRequirements },
    });
  };

  const handleChoiceChanged = (newValue, oldValue, index) => {
    const newLabels = [...labels];
    newLabels[index] = newValue;

    onCustomQuestionChanged({
      customQuestion,
      updateFields: { labels: newLabels, labelsType },
    });
  };

  const handleChoiceRemoved = (choice, index) => {
    const newLabels = labels.slice(0, index).concat(labels.slice(index + 1));

    const newChoiceRequirements = multipleSelectionQuestionRequirements({
      rangeType: customQuestion?.question?.rangeType,
      number: customQuestion?.question?.number,
      choices: newLabels,
    });

    onCustomQuestionChanged({
      customQuestion,
      updateFields: { labels: newLabels, labelsType, ...newChoiceRequirements },
    });

    setDeleteCounter(deleteCounter + 1);
  };

  const handleChoiceRequirementsChanged = ({ rangeType, number }) => {
    const newChoiceRequirements = multipleSelectionQuestionRequirements({
      rangeType,
      number,
      choices: labels,
    });

    onCustomQuestionChanged({
      customQuestion,
      updateFields: { ...newChoiceRequirements },
    });
  };

  if (type !== CustomQuestionTypes.MultiSelect) {
    return <></>;
  }

  return (
    <Box>
      <Typography mb={1}>Respondents must choose*</Typography>
      <ChoiceRequirementsSelect
        selectedRangeType={customQuestion?.question?.rangeType}
        selectedNumber={Number(customQuestion?.question?.number) || ''}
        onChange={handleChoiceRequirementsChanged}
      />
      {errors?.choiceRequirements?.length > 0 && (
        <Box my={1}>
          <ValidationErrorNotice message={serializeErrors(errors.choiceRequirements)} />
        </Box>
      )}
      <Box mb={4} />
      {showHeading && (
        <Subheader
          name={'Enter your custom choices'}
          description={'Choices will be randomized to minimize survey bias.'}
        />
      )}
      <Box my={2}>
        {labels.map((label, index) => (
          /** 
            It's VERY IMPORTANT that the key changes when the label changes and that we include the
            index so that there are no key collisions. This is because the underlying choice text field
            uses internal state and debounces updates. A consequence of this is that if not remounted, the
            internal state will not match the new label state. Unfortunately, this trades off performance 
            on deletes, since we're remounting many times when a choice gets deleted
          */
          <Box key={`${index}_${deleteCounter}`} mb={1}>
            <MultipleSelectionCustomChoice
              customChoice={label}
              isDeletable={isDeleteable}
              canDeleteChoice={canDeleteChoices}
              handleCustomChoiceChange={(newValue) => handleChoiceChanged(newValue, label, index)}
              handleCustomChoiceDelete={(value) => handleChoiceRemoved(value, index)}
            />
          </Box>
        ))}
        {errors?.labels?.length > 0 && (
          <Box my={1}>
            <ValidationErrorNotice message={serializeErrors(errors.labels)} />
          </Box>
        )}
      </Box>
      {canAddOptions && (
        <Box mt={4}>
          <AddListItemButton width="100%" size="small" onClick={handleChoiceAdded} />
        </Box>
      )}
      <Box mb={4} />
      <Typography mb={1}>Follow-up question</Typography>
      <DebouncedInput
        value={customQuestion.question.followUpQuestionText || ''}
        onChange={(value) => {
          onCustomQuestionChanged({
            customQuestion: customQuestion,
            updateFields: { followUpQuestionText: value },
          });
        }}
        debounceMs={500}
        renderInput={({ value, onChange }) => (
          <CustomTextField
            value={value}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: 14,
              },
            }}
            onChange={onChange}
          />
        )}
      />
      {errors?.followUpQuestionText?.length > 0 && (
        <Box my={1}>
          <ValidationErrorNotice message={serializeErrors(errors.followUpQuestionText)} />
        </Box>
      )}
    </Box>
  );
}

export default MultipleSelectionConfiguration;
